import { styled, css } from "styled-components";

interface CategoryButtonProps {
  $active: boolean;
}

interface CommandTitleProps {
  type: string;
}

interface ExpandArrowProps {
  $expanded: boolean;
}

const apiColors = {
  get: "#027FFE",
  post: "#22C55E",
  put: "#EBBC46",
  delete: "#EF4444",
  chargeable: "#FD6D3F",
};

export const CommandsContainer = styled.div`
  width: 100%;
`;

export const CategoriesContainer = styled.div`
  padding: 28px 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`;

export const CategoryButtonContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
  overflow: auto;
`;

export const CategoryButton = styled.button<CategoryButtonProps>`
  padding: 10px 8px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  letter-spacing: 0.03em;
  transition: 0.2s;
  font-weight: 500;
  color: ${(props) => props.theme.colors.default.default};
  position: relative;
  overflow: clip;
  display: flex;
  align-items: center;

  &:nth-of-type(6) {
    position: relative;
    display: flex;
    gap: 8px;
  }

  &:focus-within {
    background: ${(props) => props.theme.colors.default[50]};
  }

  &::before {
    z-index: -1;
    content: "";
    position: absolute;
    width: 100%;
    height: 1px;
    bottom: 0;
    left: 0;
    background: transparent;
    border-radius: 5px;
    transition: 0.3s ease-out;
  }

  ${(props) =>
  props.$active &&
  css`
      &:first-of-type {
        color: ${(props) => props.theme.colors.default.default};
      }

      &:nth-of-type(2) {
        color: ${apiColors.get};
      }

      &:nth-of-type(3) {
        color: ${apiColors.post};
      }

      &:nth-of-type(4) {
        color: ${apiColors.put};
      }

      &:nth-of-type(5) {
        color: ${apiColors.delete};
      }

      &:nth-of-type(6) {
        p {
          color: ${apiColors.chargeable} !important;
        }

        background: #fbe2da;
        background: none;

        &::before {
          background: transparent !important;
          height: 100% !important;
        }
      }
    `};

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
  }
  &:last-of-type:not(:disabled) {
    p {
      color: ${(props) => props.theme.colors.default};
    }

    &::before {
      background: ${(props) => props.theme.colors.default.default};
    }
  }

  &:disabled {
    color: #e1e1e1;
    background: none !important;
    cursor: default;
  }
`;

export const CategoryDisclaimer = styled.span`
  color: ${(props) => props.theme.colors.default.default};
  white-space: nowrap;
  font-size: 14px;

  ${({ theme }) => theme.breakpoints.sm.query} {
    white-space: revert;
  }
`;

export const CommandListContainer = styled.div``;

export const CommandContainer = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 24px;
  grid-template-areas:
    "header header"
    "request response";
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  ${({ theme }) => theme.breakpoints.lg.query} {
    display: flex;
    flex-direction: column;
    overflow: scroll;
  }
`;

export const ExpandArrowContainer = styled.span<ExpandArrowProps>`
  transition: 0.2s;
  color: ${(props) => props.theme.colors.default[400]};
  cursor: pointer;

  span {
    svg {
      width: 14px;
    }
  }

  ${(props) =>
  props.$expanded &&
  css`
      transform: rotate(180deg);

      span svg path {
        stroke: ${(props) => props.theme.colors.default.default};
      }
    `}
`;

export const ChargeableBanner = styled.span`
  color: ${apiColors.chargeable};
  padding: 4px 16px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 30px;
  margin: 0 10px 0 auto;
  background: #fbe2da;
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.01em;
  i {
    font-size: 10px;
    margin-bottom: 1px;
  }
  p {
    margin-top: 2px;
  }
  i {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.sm.query} {
    display: none;
  }
`;

export const CommandTitle = styled.h3<CommandTitleProps>`
  color: ${(props) => props.theme.colors.default.default};
  text-align: left;
  width: 100%;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: 0.03em;
  white-space: pre-wrap;
  word-wrap: anywhere;

  &:before {
    content: "${(props) => props.type}";
    color: ${(props) => apiColors[props.type.toLowerCase()]};
    font-weight: 900;
  }
`;

export const CommandDescription = styled.p`
  text-align: left;
  color: ${(props) => props.theme.colors.default[600]};
  font-size: 14px;
  margin-top: 12px;
  letter-spacing: 0.03em;
`;

export const CommandInfo = styled.div`
  cursor: pointer;
  width: 100%;
  font-weight: 500;
  border-top: 1px solid ${(props) => props.theme.colors.default[100]};
  padding: 18px 0;
  transition: 0.2s;
  grid-area: header;
  position: relative;

  &:focus-visible {
    background: ${(props) => props.theme.colors.default[100]};
  }

  ${ExpandArrowContainer} {
    position: absolute;
    font-size: 20px;
    top: 18px;
    right: 0;
  }

  &:hover {
    ${ExpandArrowContainer} {
      color: ${(props) => props.theme.colors.default.default};
    }
  }
`;

export const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;

  &:first-of-type {
    grid-area: request;
  }

  &:last-of-type {
    grid-area: response;
  }
`;
export const DetailsTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
`;
